import { useCallback, useEffect, useRef } from "react";

export default function useActaModal({
  open = true,
  onClose,
  closeOnEscape = true,
  closeOnOverlay = true,
  lockBodyScroll = true,
  restoreFocus = true,
} = {}) {
  const previouslyFocusedRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const restoreFocusRef = useRef(restoreFocus);

  onCloseRef.current = onClose;
  restoreFocusRef.current = restoreFocus;

  const requestClose = useCallback(
    (reason = "programmatic") => {
      if (!open || typeof onCloseRef.current !== "function") return false;
      if (reason === "escape" && !closeOnEscape) return false;
      if (reason === "overlay" && !closeOnOverlay) return false;
      onCloseRef.current(reason);
      return true;
    },
    [closeOnEscape, closeOnOverlay, open]
  );

  useEffect(() => {
    if (!open || typeof document === "undefined") return undefined;

    previouslyFocusedRef.current = document.activeElement;

    return () => {
      if (
        restoreFocusRef.current &&
        previouslyFocusedRef.current &&
        typeof previouslyFocusedRef.current.focus === "function"
      ) {
        previouslyFocusedRef.current.focus();
      }
    };
  }, [open]);

  useEffect(() => {
    if (!open || !lockBodyScroll || typeof document === "undefined") return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [lockBodyScroll, open]);

  useEffect(() => {
    if (!open || typeof document === "undefined") return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") requestClose("escape");
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, requestClose]);

  const onOverlayMouseDown = useCallback(
    (event) => {
      if (event.target === event.currentTarget) requestClose("overlay");
    },
    [requestClose]
  );

  return {
    requestClose,
    overlayProps: { onMouseDown: onOverlayMouseDown },
    dialogProps: {
      role: "dialog",
      "aria-modal": "true",
      onMouseDown: (event) => event.stopPropagation(),
    },
  };
}
